import React, { useEffect, useState } from 'react'
import { CheckIcon, Loader2Icon, PencilIcon, PlusIcon, TagIcon, Trash2Icon, XIcon } from 'lucide-react'
import {
  ConversationLabel,
  LABEL_COLORS,
  createConversationLabel,
  deleteConversationLabel,
  fetchConversationLabels,
  fetchLabelAssignments,
  updateConversationLabel,
} from '../../api/conversationLabels'

/**
 * Settings → Labels (communication.inbox_admin). Manage the tenant's Inbox
 * label catalog: create, rename, recolor, delete. Deleting a label only
 * removes the tag from conversations — the conversations themselves are
 * untouched. Everyday label creation also happens inline in the Inbox picker;
 * this screen is for tidy-up.
 */
export function LabelSettings() {
  const [labels, setLabels] = useState<ConversationLabel[]>([])
  const [usageCounts, setUsageCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(LABEL_COLORS[0])
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    try {
      const [labelList, assignments] = await Promise.all([
        fetchConversationLabels(),
        fetchLabelAssignments(),
      ])
      setLabels(labelList)
      const counts: Record<string, number> = {}
      for (const row of assignments) counts[row.label_id] = (counts[row.label_id] || 0) + 1
      setUsageCounts(counts)
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to load labels: ' + (err.message || 'Unknown error') })
    } finally {
      setLoading(false)
    }
  }

  function flash(type: 'success' | 'error', text: string) {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 4000)
  }

  async function handleCreate() {
    const name = newName.trim()
    if (!name || creating) return
    if (labels.some((l) => l.name.toLowerCase() === name.toLowerCase())) {
      flash('error', 'A label with this name already exists.')
      return
    }
    setCreating(true)
    try {
      await createConversationLabel(name, newColor)
      setNewName('')
      await load()
      flash('success', `Label "${name}" created.`)
    } catch (err: any) {
      flash('error', 'Failed to create label: ' + (err.message || 'Unknown error'))
    } finally {
      setCreating(false)
    }
  }

  async function handleRename(label: ConversationLabel) {
    const name = editName.trim()
    if (!name || name === label.name) {
      setEditingId(null)
      return
    }
    if (labels.some((l) => l.id !== label.id && l.name.toLowerCase() === name.toLowerCase())) {
      flash('error', 'A label with this name already exists.')
      return
    }
    setBusyId(label.id)
    try {
      await updateConversationLabel(label.id, { name })
      setLabels((prev) => prev.map((l) => (l.id === label.id ? { ...l, name } : l)))
      setEditingId(null)
    } catch (err: any) {
      flash('error', 'Failed to rename label: ' + (err.message || 'Unknown error'))
    } finally {
      setBusyId(null)
    }
  }

  async function handleRecolor(label: ConversationLabel, color: string) {
    if (color === label.color) return
    setBusyId(label.id)
    try {
      await updateConversationLabel(label.id, { color })
      setLabels((prev) => prev.map((l) => (l.id === label.id ? { ...l, color } : l)))
    } catch (err: any) {
      flash('error', 'Failed to update color: ' + (err.message || 'Unknown error'))
    } finally {
      setBusyId(null)
    }
  }

  async function handleDelete(label: ConversationLabel) {
    const count = usageCounts[label.id] || 0
    const suffix =
      count > 0
        ? ` It is on ${count} conversation${count === 1 ? '' : 's'}; the tag will be removed from them (the conversations are not deleted).`
        : ''
    if (!window.confirm(`Delete the label "${label.name}"?${suffix}`)) return
    setBusyId(label.id)
    try {
      await deleteConversationLabel(label.id)
      setLabels((prev) => prev.filter((l) => l.id !== label.id))
      flash('success', `Label "${label.name}" deleted.`)
    } catch (err: any) {
      flash('error', 'Failed to delete label: ' + (err.message || 'Unknown error'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <TagIcon className="w-5 h-5 text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">Inbox Labels</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Color-coded tags for filing conversations (e.g. Violations, Urgent, Billing). Anyone with
        Inbox access can create and apply labels; renaming, recoloring, and deleting happens here.
      </p>

      {message && (
        <div
          className={`mb-4 p-3 rounded-xl border text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-6">
          <Loader2Icon className="w-4 h-4 animate-spin" />
          Loading labels...
        </div>
      ) : (
        <div className="space-y-2">
          {labels.length === 0 && (
            <p className="text-sm text-gray-400 py-2">
              No labels yet. Create one below, or from the label picker in the Inbox.
            </p>
          )}

          {labels.map((label) => {
            const busy = busyId === label.id
            const editing = editingId === label.id
            return (
              <div
                key={label.id}
                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl"
              >
                <span
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: label.color }}
                />

                {editing ? (
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename(label)}
                      maxLength={40}
                      autoFocus
                      className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={() => handleRename(label)}
                      disabled={busy}
                      className="p-1.5 rounded-lg text-green-600 hover:bg-green-50"
                      title="Save name"
                    >
                      {busy ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <CheckIcon className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"
                      title="Cancel"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{label.name}</p>
                    <p className="text-xs text-gray-400">
                      {usageCounts[label.id] || 0} conversation{(usageCounts[label.id] || 0) === 1 ? '' : 's'}
                    </p>
                  </div>
                )}

                {/* Color swatches */}
                <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
                  {LABEL_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleRecolor(label, color)}
                      disabled={busy}
                      className={`w-4 h-4 rounded-full transition-transform hover:scale-110 ${
                        label.color === color ? 'ring-2 ring-offset-1 ring-gray-400' : ''
                      }`}
                      style={{ backgroundColor: color }}
                      title="Set color"
                    />
                  ))}
                </div>

                {!editing && (
                  <button
                    onClick={() => {
                      setEditingId(label.id)
                      setEditName(label.name)
                    }}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
                    title="Rename"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(label)}
                  disabled={busy}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                  title="Delete label"
                >
                  <Trash2Icon className="w-4 h-4" />
                </button>
              </div>
            )
          })}

          {/* Create new */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 border border-dashed border-gray-300 rounded-xl">
            <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: newColor }} />
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="New label name..."
              maxLength={40}
              className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            />
            <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
              {LABEL_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setNewColor(color)}
                  className={`w-4 h-4 rounded-full transition-transform hover:scale-110 ${
                    newColor === color ? 'ring-2 ring-offset-1 ring-gray-400' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  title="Pick color"
                />
              ))}
            </div>
            <button
              onClick={handleCreate}
              disabled={!newName.trim() || creating}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors flex-shrink-0"
            >
              {creating ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <PlusIcon className="w-4 h-4" />}
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
